import { VoxelAsset, VoxelData } from '../voxelTypes';
import { PALETTE, getRandomizedColor } from '../voxelColor';

export type CropType = 'wheat_field' | 'pumpkin_patch' | 'flower_garden';

export function generateVoxelCropField(
  id: string,
  name: string,
  type: CropType = 'wheat_field',
  width = 10,
  depth = 10
): VoxelAsset {
  const voxels: VoxelData[] = [];

  // Ground soil base
  for (let x = 0; x < width; x++) {
    for (let z = 0; z < depth; z++) {
      const isFurrow = (x + z) % 2 === 0;
      voxels.push({
        x, y: 0, z,
        color: isFurrow ? PALETTE.DIRT_DARK : PALETTE.DIRT_MEDIUM,
        materialType: 'opaque'
      });
    }
  }

  // -----------------------------------------------------------------
  // 1. WHEAT FIELD (High-Detail Micro-Voxel Golden Harvest)
  // -----------------------------------------------------------------
  if (type === 'wheat_field') {
    // Tilled earth rows with furrows
    for (let x = 0; x < width; x++) {
      for (let z = 0; z < depth; z++) {
        const isFurrow = z % 2 === 0;
        voxels.push({
          x, y: 0, z,
          color: isFurrow ? PALETTE.DIRT_DARK : PALETTE.DIRT_MEDIUM,
          materialType: 'opaque'
        });
      }
    }

    // Dense Golden Wheat Stalks with micro-voxel grain heads
    for (let x = 0.2; x < width; x += 0.5) {
      for (let z = 0.2; z < depth; z += 0.5) {
        if (z % 1 !== 0) { // Plant in tilled rows
          const heightOffset = Math.sin(x * 3 + z * 2) * 0.3;
          const stalkHeight = 1.8 + heightOffset;

          // Slender green/golden stem
          voxels.push({
            x, y: 0.8, z,
            color: PALETTE.WHEAT_STEM,
            materialType: 'foliage',
            scaleX: 0.22, scaleZ: 0.22, scaleY: 0.8
          });
          voxels.push({
            x, y: 1.4, z,
            color: PALETTE.WHEAT_STEM,
            materialType: 'foliage',
            scaleX: 0.22, scaleZ: 0.22, scaleY: 0.8
          });

          // Plump Golden Grain Head
          voxels.push({
            x, y: stalkHeight + 0.2, z,
            color: getRandomizedColor(PALETTE.WHEAT_GOLD, 0.03),
            materialType: 'foliage',
            scaleX: 0.38, scaleZ: 0.38, scaleY: 0.6
          });
          voxels.push({
            x, y: stalkHeight + 0.5, z,
            color: getRandomizedColor(PALETTE.WHEAT_GOLD, 0.03),
            materialType: 'foliage',
            scaleX: 0.3, scaleZ: 0.3, scaleY: 0.4
          });
        }
      }
    }

    // Split-Rail Wooden Perimeter Fence with Corner Posts
    for (let x = -0.5; x <= width + 0.5; x += 1) {
      const isCornerX = x === -0.5 || x === width + 0.5;
      voxels.push({ x, y: 1, z: -0.5, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.5 });
      voxels.push({ x, y: 1, z: depth + 0.5, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.5 });
      if (isCornerX) {
        voxels.push({ x, y: 1.5, z: -0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
        voxels.push({ x, y: 1.5, z: depth + 0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
      }
    }

    // Detailed Micro Scarecrow in center (Raised up so it towers clearly above the golden wheat stalks)
    const scX = Math.floor(width / 2);
    const scZ = Math.floor(depth / 2);

    // Tall Wooden Support Post
    voxels.push({ x: scX, y: 0.8, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.25, scaleZ: 0.25, scaleY: 0.8 });
    voxels.push({ x: scX, y: 1.6, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.25, scaleZ: 0.25, scaleY: 0.8 });
    voxels.push({ x: scX, y: 2.4, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.25, scaleZ: 0.25, scaleY: 0.8 });
    voxels.push({ x: scX, y: 3.2, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.25, scaleZ: 0.25, scaleY: 0.8 });

    // Wooden Cross-beam for arms
    voxels.push({ x: scX, y: 3.8, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 1.4, scaleZ: 0.2, scaleY: 0.2 });

    // Patchwork Coat & Straw Arms
    voxels.push({ x: scX, y: 3.8, z: scZ, color: PALETTE.ROOF_RED_CLAY, materialType: 'opaque', scale: 0.65 });
    voxels.push({ x: scX - 0.65, y: 3.85, z: scZ, color: PALETTE.THATCH_YELLOW, materialType: 'opaque', scale: 0.38 });
    voxels.push({ x: scX + 0.65, y: 3.85, z: scZ, color: PALETTE.THATCH_YELLOW, materialType: 'opaque', scale: 0.38 });

    // Pumpkin Head & Felt Hat
    voxels.push({ x: scX, y: 4.4, z: scZ, color: PALETTE.PUMPKIN_ORANGE, materialType: 'opaque', scale: 0.6 });
    voxels.push({ x: scX, y: 4.8, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.85, scaleZ: 0.85, scaleY: 0.15 });
    voxels.push({ x: scX, y: 5.0, z: scZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.4 });
  }

  // -----------------------------------------------------------------
  // 2. PUMPKIN PATCH (Micro-voxel Plump Ribbed Pumpkins & Vines)
  // -----------------------------------------------------------------
  else if (type === 'pumpkin_patch') {
    // Dark rich soil beds with furrows
    for (let x = 0; x < width; x++) {
      for (let z = 0; z < depth; z++) {
        const isMound = (x % 3 !== 0) && (z % 3 !== 0);
        if (isMound) {
          voxels.push({ x, y: 1, z, color: PALETTE.DIRT_DARK, materialType: 'opaque' });
        }
      }
    }

    // Sprawling leafy vines crawling between mounds (micro-voxels scale 0.5)
    for (let x = 0; x < width; x += 0.5) {
      for (let z = 0; z < depth; z += 0.5) {
        if ((Math.sin(x * 1.5) + Math.cos(z * 1.5)) > 0.8) {
          voxels.push({
            x, y: 1.1, z,
            color: (x + z) % 1 === 0 ? PALETTE.LEAF_PINE_DARK : PALETTE.PUMPKIN_STEM,
            materialType: 'foliage',
            scale: 0.5
          });
        }
      }
    }

    // Detailed 3D Ribbed Pumpkins using 0.5 micro-voxels
    const pumpkinLocs = [
      { cx: 1.5, cz: 2.0, r: 1.2, type: 'giant' },
      { cx: 6.5, cz: 1.5, r: 1.2, type: 'giant' },
      { cx: 2.5, cz: 6.5, r: 0.9, type: 'medium' },
      { cx: 7.5, cz: 6.5, r: 1.2, type: 'giant' },
      { cx: 4.5, cz: 4.5, r: 0.8, type: 'medium' },
      { cx: 8.5, cz: 3.5, r: 0.6, type: 'small' },
      { cx: 0.8, cz: 8.2, r: 0.9, type: 'medium' },
      { cx: 5.5, cz: 8.2, r: 1.1, type: 'giant' },
    ];

    pumpkinLocs.forEach(p => {
      const step = 0.45;
      for (let dx = -p.r; dx <= p.r; dx += step) {
        for (let dz = -p.r; dz <= p.r; dz += step) {
          for (let dy = 0; dy <= p.r * 1.3; dy += step) {
            const dist = Math.hypot(dx, dz);
            if (dist <= p.r) {
              // Create ribbed pumpkin lobes using angle modulation
              const angle = Math.atan2(dz, dx);
              const isRibIndent = Math.sin(angle * 6) < -0.3;
              const color = isRibIndent ? PALETTE.PUMPKIN_DARK : PALETTE.PUMPKIN_ORANGE;

              voxels.push({
                x: p.cx + dx,
                y: 1.2 + dy,
                z: p.cz + dz,
                color,
                materialType: 'opaque',
                scale: 0.48
              });
            }
          }
        }
      }

      // Curly Green Stem on top
      voxels.push({
        x: p.cx, y: 1.2 + p.r * 1.35 + 0.1, z: p.cz,
        color: PALETTE.PUMPKIN_STEM, materialType: 'foliage', scale: 0.4
      });
      voxels.push({
        x: p.cx + 0.2, y: 1.2 + p.r * 1.35 + 0.3, z: p.cz + 0.1,
        color: PALETTE.PUMPKIN_STEM, materialType: 'foliage', scale: 0.35
      });
    });

    // Split-rail wooden fence perimeter
    for (let x = -0.5; x <= width + 0.5; x += 1) {
      voxels.push({ x, y: 1, z: -0.5, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque' });
      voxels.push({ x, y: 1, z: depth + 0.5, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque' });
    }

    // Wooden Farm Sign with carved pumpkin icon
    const signX = width - 1.5;
    const signZ = 0.5;
    voxels.push({ x: signX, y: 1, z: signZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque' });
    voxels.push({ x: signX, y: 2, z: signZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque' });
    voxels.push({ x: signX - 0.5, y: 2, z: signZ, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.6 });
    voxels.push({ x: signX + 0.5, y: 2, z: signZ, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.6 });
    voxels.push({ x: signX, y: 2, z: signZ + 0.3, color: PALETTE.PUMPKIN_ORANGE, materialType: 'opaque', scale: 0.5 });
  }

  // -----------------------------------------------------------------
  // 3. FLOWER GARDEN (High-Detail Micro-Voxel Botanical Sanctuary)
  // -----------------------------------------------------------------
  else if (type === 'flower_garden') {
    // Flagstone Garden Pathway
    for (let z = 0; z < depth; z += 0.8) {
      voxels.push({ x: 4.5, y: 1.05, z, color: PALETTE.STONE_LIGHT, materialType: 'opaque', scaleX: 1.6, scaleZ: 0.7, scaleY: 0.2 });
    }

    // Cobblestone border edging along beds
    for (let z = 0; z < depth; z += 0.6) {
      voxels.push({ x: 3.2, y: 1.1, z, color: PALETTE.COBBLE, materialType: 'opaque', scale: 0.5 });
      voxels.push({ x: 5.8, y: 1.1, z, color: PALETTE.COBBLE, materialType: 'opaque', scale: 0.5 });
    }

    // 1. Red & Pink Rose Bushes (x = 0.5..2.8, z = 0.8..4.2)
    for (let rx = 0.8; rx <= 2.8; rx += 0.9) {
      for (let rz = 0.8; rz <= 4.2; rz += 0.9) {
        // Dark green leafy bush base
        for (let bx = -0.3; bx <= 0.3; bx += 0.3) {
          for (let bz = -0.3; bz <= 0.3; bz += 0.3) {
            voxels.push({
              x: rx + bx, y: 1.3, z: rz + bz,
              color: PALETTE.LEAF_OAK_LUSH, materialType: 'foliage', scale: 0.5
            });
            voxels.push({
              x: rx + bx, y: 1.6, z: rz + bz,
              color: PALETTE.LEAF_OAK_LUSH, materialType: 'foliage', scale: 0.45
            });
          }
        }
        // Micro Rose Blooms (Crimson Red & Soft Pink)
        const isPink = (rx + rz) % 2 === 0;
        const roseColor = isPink ? 0xff6688 : PALETTE.FLOWER_RED;
        voxels.push({ x: rx - 0.2, y: 1.8, z: rz, color: roseColor, materialType: 'foliage', scale: 0.4 });
        voxels.push({ x: rx + 0.2, y: 1.9, z: rz + 0.1, color: roseColor, materialType: 'foliage', scale: 0.4 });
        voxels.push({ x: rx, y: 2.0, z: rz - 0.2, color: roseColor, materialType: 'foliage', scale: 0.4 });
      }
    }

    // 2. Lavender Stalks (x = 0.8..2.8, z = 5.2..8.8)
    for (let lx = 0.8; lx <= 2.8; lx += 0.6) {
      for (let lz = 5.2; lz <= 8.8; lz += 0.6) {
        // Slender green stem
        voxels.push({ x: lx, y: 1.2, z: lz, color: PALETTE.WHEAT_STEM, materialType: 'foliage', scaleX: 0.2, scaleZ: 0.2, scaleY: 0.8 });
        voxels.push({ x: lx, y: 1.6, z: lz, color: PALETTE.WHEAT_STEM, materialType: 'foliage', scaleX: 0.2, scaleZ: 0.2, scaleY: 0.8 });
        // Purple micro flower cluster head
        voxels.push({ x: lx, y: 2.0, z: lz, color: PALETTE.FLOWER_PURPLE, materialType: 'foliage', scale: 0.45 });
        voxels.push({ x: lx, y: 2.2, z: lz, color: 0x9955ff, materialType: 'foliage', scale: 0.35 });
      }
    }

    // 3. Golden Sunflowers (x = 6.2..8.8, z = 0.8..4.2)
    for (let sx = 6.4; sx <= 8.8; sx += 1.0) {
      for (let sz = 1.0; sz <= 4.2; sz += 1.2) {
        // Tall sturdy stem
        for (let sy = 1.2; sy <= 2.6; sy += 0.4) {
          voxels.push({ x: sx, y: sy, z: sz, color: PALETTE.WHEAT_STEM, materialType: 'foliage', scaleX: 0.25, scaleZ: 0.25, scaleY: 0.5 });
        }
        // Broad green side leaves
        voxels.push({ x: sx - 0.3, y: 1.8, z: sz, color: PALETTE.LEAF_OAK_LIGHT, materialType: 'foliage', scale: 0.4 });
        voxels.push({ x: sx + 0.3, y: 2.1, z: sz, color: PALETTE.LEAF_OAK_LIGHT, materialType: 'foliage', scale: 0.4 });

        // Sun-facing Golden Petal Ring & Dark Seed Center
        const headY = 2.8;
        voxels.push({ x: sx, y: headY, z: sz - 0.1, color: PALETTE.SUNFLOWER_CENTER, materialType: 'foliage', scale: 0.45 });
        // Petal ring
        for (let pAngle = 0; pAngle < 360; pAngle += 45) {
          const pr = (pAngle * Math.PI) / 180;
          voxels.push({
            x: sx + Math.cos(pr) * 0.35,
            y: headY + Math.sin(pr) * 0.35,
            z: sz - 0.1,
            color: PALETTE.FLOWER_YELLOW,
            materialType: 'foliage',
            scale: 0.35
          });
        }
      }
    }

    // 4. Blue Hydrangeas & White Lilies (x = 6.2..8.8, z = 5.2..8.8)
    for (let hx = 6.2; hx <= 8.8; hx += 0.8) {
      for (let hz = 5.2; hz <= 8.8; hz += 0.8) {
        const isHydrangea = (hx + hz) % 2 === 0;
        voxels.push({ x: hx, y: 1.3, z: hz, color: PALETTE.LEAF_OAK_LUSH, materialType: 'foliage', scale: 0.5 });
        if (isHydrangea) {
          // Cloud-like blue hydrangea head
          voxels.push({ x: hx, y: 1.7, z: hz, color: PALETTE.FLOWER_BLUE, materialType: 'foliage', scale: 0.6 });
          voxels.push({ x: hx + 0.1, y: 1.9, z: hz, color: 0x88ccff, materialType: 'foliage', scale: 0.4 });
        } else {
          // White lily trumpet blossom
          voxels.push({ x: hx, y: 1.8, z: hz, color: PALETTE.FLOWER_WHITE, materialType: 'foliage', scale: 0.45 });
          voxels.push({ x: hx, y: 1.9, z: hz - 0.1, color: PALETTE.FLOWER_YELLOW, materialType: 'foliage', scale: 0.25 });
        }
      }
    }

    // Wooden Trellis Entrance Arch over path (z = 0)
    for (let ay = 1.0; ay <= 3.8; ay += 0.5) {
      voxels.push({ x: 3.2, y: ay, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.6 });
      voxels.push({ x: 5.8, y: ay, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.6 });
    }
    // Archtop beam
    for (let ax = 3.2; ax <= 5.8; ax += 0.4) {
      voxels.push({ x: ax, y: 3.8, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.6 });
      // Climbing roses on arch
      voxels.push({ x: ax, y: 4.2, z: 0, color: ax % 2 === 0 ? PALETTE.FLOWER_RED : PALETTE.LEAF_OAK_LUSH, materialType: 'foliage', scale: 0.45 });
    }

    // Decorative Wooden Garden Bench
    const benchX = 1.8, benchZ = 4.8;
    for (let bx = -0.8; bx <= 0.8; bx += 0.4) {
      voxels.push({ x: benchX + bx, y: 1.4, z: benchZ, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scaleX: 0.4, scaleY: 0.1, scaleZ: 0.6 });
    }
    voxels.push({ x: benchX - 0.7, y: 1.1, z: benchZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.3 });
    voxels.push({ x: benchX + 0.7, y: 1.1, z: benchZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.3 });
  }

  return {
    id,
    name,
    category: 'prop',
    description: `Micro-voxel ${type.replace('_', ' ')}`,
    voxels,
    dimensions: { x: width, y: 5, z: depth }
  };
}
